'use client';

import shuffle from 'lodash/shuffle';
import { useEffect, useRef, useState } from 'react';
import { useIntersection } from 'react-use';

import Article, { ArticleLabels } from './Article';

import { AnalyticsParams } from '@components/Analytics/Google/GAScript';
import Loader from '@components/root/Loader';
import { logger } from '@logger';
import { fetchApi } from '@utils/api/client';
import { ArticleViewType, ProcessedArticleI, RelatedArticleI } from 'types/article';

interface ArticleResponse {
  article: ProcessedArticleI;
  viewType: ArticleViewType;
}

interface ArticleInfiniteScrollProps {
  relatedArticles: RelatedArticleI[];
  labels: ArticleLabels;
  analyticsData: AnalyticsParams;
}

export default function ArticleInfiniteScroll({ relatedArticles, labels, analyticsData }: ArticleInfiniteScrollProps) {
  const [articles, setArticles] = useState<ArticleResponse[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasReachedEnd, setHasReachedEnd] = useState(false);

  const intersectionRef = useRef<HTMLDivElement>(null);
  const intersection = useIntersection(intersectionRef, {
    root: null,
    rootMargin: '0px',
    threshold: 1,
  });

  const fetchArticle = async () => {
    if (isLoading || hasReachedEnd) {
      return;
    }

    setIsLoading(true);

    const lastPost = articles[articles.length - 1];
    const nextArticle = shuffle(lastPost?.article.relatedPosts || relatedArticles)[0];

    if (!nextArticle) {
      setHasReachedEnd(true);
      setIsLoading(false);
      return;
    }

    try {
      const article = await fetchApi<ArticleResponse>(`/posts/${nextArticle.slug}`);

      if (article.status !== 200) {
        throw new Error('Article not found');
      }

      setArticles((articles) => [...articles, article.data]);
    } catch (e) {
      logger.error('Failed to fetch next article!');
      setHasReachedEnd(true);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!intersection || intersection.intersectionRatio < 1) {
      return;
    }

    fetchArticle();
  }, [intersection?.intersectionRatio]);

  return (
    <div className="">
      {articles.map(({ article, viewType }) => (
        <Article
          key={article.slug}
          article={article}
          mainArticle={false}
          viewType={viewType}
          labels={labels}
          analyticsData={analyticsData}
        />
      ))}
      {!hasReachedEnd && (
        <div className="flex items-center justify-center" ref={intersectionRef}>
          <Loader />
        </div>
      )}
    </div>
  );
}
